import { LogTresoType, ManualComptaLine } from "compta-shared";
import _ from "underts";

const sum = (sequence: { montantCent: number }[]) =>
  sequence.reduce((result, item) => result + item.montantCent, 0);

interface Candidat {
  montantCent: number;
  _id: string;
  date?: Date | string;
}

interface CombinaisonSearchLimits {
  maxResults?: number;
  maxVisitedNodes?: number;
  maxDurationMs?: number;
}

interface CombinaisonSearchOptions extends CombinaisonSearchLimits {
  prioritizeSameDayAs?: Date;
}

interface CombinaisonSearchResult {
  combinaisons: Candidat[][];
  hasReachedLimit: boolean;
}

const DEFAULT_SEARCH_LIMITS: Required<CombinaisonSearchLimits> = {
  maxResults: Number.POSITIVE_INFINITY,
  maxVisitedNodes: Number.POSITIVE_INFINITY,
  maxDurationMs: Number.POSITIVE_INFINITY,
};

const getDayStartMs = (dateValue?: Date | string) => {
  if (!dateValue) {
    return null;
  }

  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  const time = date.getTime();

  if (Number.isNaN(time)) {
    return null;
  }

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0,
  ).getTime();
};

const sortCandidatesByDatePriority = (
  candidates: Candidat[],
  prioritizeSameDayAs?: Date,
) => {
  if (!prioritizeSameDayAs) {
    return candidates;
  }

  const preferredDayStartMs = getDayStartMs(prioritizeSameDayAs);
  if (preferredDayStartMs == null) {
    return candidates;
  }

  return candidates
    .map((candidate, originalIndex) => ({ candidate, originalIndex }))
    .sort((a, b) => {
      const dayA = getDayStartMs(a.candidate.date);
      const dayB = getDayStartMs(b.candidate.date);

      const isSameDayA = dayA === preferredDayStartMs;
      const isSameDayB = dayB === preferredDayStartMs;
      if (isSameDayA !== isSameDayB) {
        return isSameDayA ? -1 : 1;
      }

      const distanceA =
        dayA == null
          ? Number.POSITIVE_INFINITY
          : Math.abs(dayA - preferredDayStartMs);
      const distanceB =
        dayB == null
          ? Number.POSITIVE_INFINITY
          : Math.abs(dayB - preferredDayStartMs);
      if (distanceA !== distanceB) {
        return distanceA - distanceB;
      }

      return a.originalIndex - b.originalIndex;
    })
    .map(({ candidate }) => candidate);
};

const getCombinaisons = ({
  candidats,
  target,
  count,
  includedIds = [],
  limits,
}: {
  candidats: Candidat[];
  target: number;
  includedIds?: string[];
  count?: number;
  limits?: CombinaisonSearchOptions;
}): CombinaisonSearchResult => {
  const startedAt = Date.now();
  const { prioritizeSameDayAs, ...limitOverrides } = limits || {};
  const mergedLimits = {
    ...DEFAULT_SEARCH_LIMITS,
    ...limitOverrides,
  };
  const results: Candidat[][] = [];
  let hasReachedLimit = false;
  let visitedNodes = 0;

  const includedSet = new Set(includedIds);
  const includedCandidates: Candidat[] = [];
  const otherCandidates: Candidat[] = [];

  candidats.forEach((candidate) => {
    if (includedSet.has(candidate._id)) {
      includedCandidates.push(candidate);
    } else {
      otherCandidates.push(candidate);
    }
  });

  const sortedOtherCandidates = sortCandidatesByDatePriority(
    otherCandidates,
    prioritizeSameDayAs,
  );

  const initialCombinaison = [...includedCandidates];
  const initialSum = sum(initialCombinaison);

  if (initialSum > target) {
    return { combinaisons: [], hasReachedLimit: false };
  }

  if (typeof count === "number" && initialCombinaison.length > count) {
    return { combinaisons: [], hasReachedLimit: false };
  }

  if (typeof count === "number" && initialCombinaison.length === count) {
    return {
      combinaisons: initialSum === target ? [initialCombinaison] : [],
      hasReachedLimit: false,
    };
  }

  if (typeof count !== "number" && initialSum === target) {
    if (initialCombinaison.length === 0) {
      // Continue searching to find non-empty zero-sum combinations if any exist.
    } else {
      return {
        combinaisons: [initialCombinaison],
        hasReachedLimit: false,
      };
    }
  }

  const suffixPositiveSums = new Array(sortedOtherCandidates.length + 1).fill(
    0,
  );
  for (let i = sortedOtherCandidates.length - 1; i >= 0; i -= 1) {
    suffixPositiveSums[i] =
      suffixPositiveSums[i + 1] +
      Math.max(0, sortedOtherCandidates[i].montantCent);
  }

  const hasTimeBudgetExceeded = () =>
    Date.now() - startedAt >= mergedLimits.maxDurationMs;

  const shouldStopSearch = () => {
    if (results.length >= mergedLimits.maxResults) {
      hasReachedLimit = true;
      return true;
    }
    if (visitedNodes >= mergedLimits.maxVisitedNodes) {
      hasReachedLimit = true;
      return true;
    }
    if (hasTimeBudgetExceeded()) {
      hasReachedLimit = true;
      return true;
    }
    return false;
  };

  const walk = (
    startIndex: number,
    currentSum: number,
    currentCombinaison: Candidat[],
  ) => {
    if (shouldStopSearch()) {
      return;
    }

    visitedNodes += 1;

    if (typeof count === "number") {
      const currentLength = currentCombinaison.length;
      const remainingSlots = count - currentLength;

      if (remainingSlots === 0) {
        if (currentSum === target) {
          results.push([...currentCombinaison]);
        }
        return;
      }

      const remainingCandidates = sortedOtherCandidates.length - startIndex;
      if (remainingSlots > remainingCandidates) {
        return;
      }

      if (remainingSlots === 1) {
        for (let i = startIndex; i < sortedOtherCandidates.length; i += 1) {
          if (shouldStopSearch()) {
            return;
          }
          if (currentSum + sortedOtherCandidates[i].montantCent === target) {
            results.push([...currentCombinaison, sortedOtherCandidates[i]]);
          }
        }
        return;
      }
    } else {
      if (currentSum === target && currentCombinaison.length > 0) {
        results.push([...currentCombinaison]);
        return;
      }
    }

    if (startIndex >= sortedOtherCandidates.length) {
      return;
    }

    if (currentSum + suffixPositiveSums[startIndex] < target) {
      return;
    }

    for (let i = startIndex; i < sortedOtherCandidates.length; i += 1) {
      if (shouldStopSearch()) {
        return;
      }

      const candidate = sortedOtherCandidates[i];
      const nextSum = currentSum + candidate.montantCent;

      if (nextSum > target) {
        continue;
      }

      currentCombinaison.push(candidate);
      walk(i + 1, nextSum, currentCombinaison);
      currentCombinaison.pop();
    }
  };

  walk(0, initialSum, [...initialCombinaison]);

  return { combinaisons: results, hasReachedLimit };
};

//target est la somme à atteindre
//count est optionnel, indique le nombre de candidats à éventuellement respecter dans la combinaison
const montantCombinaisons = (
  candidats: Candidat[],
  target: number,
  count?: number,
  limits?: CombinaisonSearchOptions,
) => {
  return getCombinaisons({
    candidats,
    target,
    count,
    limits,
  }).combinaisons;
};

//target est la somme à atteindre
//count est optionnel, indique le nombre de candidats à éventuellement respecter dans la combinaison
const montantCombinaisons2 = (
  candidats: Candidat[],
  target: number,
  included: string[],
  count?: number,
  limits?: CombinaisonSearchOptions,
) => {
  return getCombinaisons({
    candidats,
    target,
    includedIds: included,
    count,
    limits,
  }).combinaisons;
};

const montantCombinaisons2WithMeta = (
  candidats: Candidat[],
  target: number,
  included: string[],
  count?: number,
  limits?: CombinaisonSearchOptions,
) => {
  return getCombinaisons({
    candidats,
    target,
    includedIds: included,
    count,
    limits,
  });
};

const isNested = <U extends any | any[]>(arr: U[]) =>
  Array.isArray(arr) && arr.some((i) => Array.isArray(i));

const isDoubleNested = <U extends any[] | any[][]>(arr: U[]) =>
  Array.isArray(arr) && arr.some(isNested);

const isTripleNested = <U extends any[][] | any[][][]>(arr: U[]) =>
  Array.isArray(arr) && arr.some(isDoubleNested);

const candidatesFlagger = (
  candidates: (LogTresoType | ManualComptaLine)[],
  combinaisons: Candidat[][],
) => {
  const proposedByCandidate = combinaisons.reduce(
    (result, combinaison, combinaisonNumber) => {
      combinaison.forEach((combi) => {
        if (!result[combi._id]) {
          result[combi._id] = [];
        }
        result[combi._id].push(combinaisonNumber);
      });
      return result;
    },
    {} as { [candidateId: string]: number[] },
  );

  return candidates.map((candidate) => ({
    ...candidate,
    proposed: proposedByCandidate[candidate._id] || [],
  }));
};

const combinaisonToIds = (combinaison: Candidat[]) => {
  return combinaison.map((c) => c._id);
};

const combinaisonsToUniqueIds = (combinaisons: Candidat[][]) => {
  return _.uniq(combinaisons.map(combinaisonToIds).flat(1));
};

const test = { isTripleNested, isDoubleNested, isNested };

export {
  test,
  candidatesFlagger,
  montantCombinaisons,
  combinaisonsToUniqueIds,
  montantCombinaisons2,
  montantCombinaisons2WithMeta,
};
